import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Brain, Info } from 'lucide-react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { MUNICIPIOS, AREAS } from '../../utils/cidades';

const HARD_SKILLS_SUGESTOES = [
  'Excel / Google Sheets','Word / Google Docs','PowerPoint','Sistema ERP','Sistema CRM',
  'Programação Python','Programação JavaScript','SQL / Banco de dados','Power BI','Análise de dados',
  'Gestão de equipes','Contabilidade','Nota fiscal','Atendimento ao cliente','Redes de computadores',
  'Suporte técnico','Inglês','Espanhol','Direção veicular','Operação de máquinas',
  'Pacote Office','AutoCAD','Photoshop','Marketing digital','Vendas externas',
];

const IDIOMAS_LISTA = ['Inglês','Espanhol','Francês','Italiano','Mandarim','Alemão'];

// Cursos pré-definidos por área (sugestões inteligentes)
const CURSOS_SUGESTOES = [
  // Saúde
  'Curso de Atendente de Farmácia','Técnico em Enfermagem','Técnico em Radiologia',
  'Auxiliar de Saúde Bucal','CRM ativo','CRF ativo','COREN ativo',
  // TI
  'Técnico em Informática','Técnico em Redes','Certificação CompTIA A+',
  'Certificação CISCO CCNA','Graduação em Sistemas de Informação','Graduação em TI',
  // Educação
  'Licenciatura na área','Magistério','Pós-graduação em Educação',
  // Direito
  'OAB ativa','Bacharel em Direito',
  // Contabilidade
  'CRC ativo','Técnico em Contabilidade','Bacharel em Contabilidade',
  // Construção
  'CREA ativo','Técnico em Edificações','Técnico em Segurança do Trabalho','NR-35','NR-10','NR-12',
  // Veículos
  'CNH categoria A','CNH categoria B','CNH categoria C','CNH categoria D','CNH categoria E',
  // Outros
  'Curso de Vigilante','Curso de Bombeiro Civil','CPA-10','CPA-20','CEA',
];

export default function NovaVaga() {
  const { register, handleSubmit, formState:{ errors } } = useForm();
  const [areas,       setAreas]       = useState([]);
  const [hardSkills,  setHardSkills]  = useState([]);
  const [softSkills,  setSoftSkills]  = useState([]);
  const [idiomas,     setIdiomas]     = useState([]);
  const [cursos,      setCursos]      = useState([]); // cursos/certificações obrigatórias
  const [novaSkill,   setNovaSkill]   = useState('');
  const [novoCurso,   setNovoCurso]   = useState('');
  const [saving,      setSaving]      = useState(false);
  const navigate = useNavigate();

  const toggleArea  = a => setAreas(p     => p.includes(a) ? p.filter(x=>x!==a) : [...p,a]);
  const toggleSkill = s => setHardSkills(p => p.includes(s) ? p.filter(x=>x!==s) : [...p,s]);
  const toggleSoft  = s => setSoftSkills(p => p.includes(s) ? p.filter(x=>x!==s) : [...p,s]);
  const toggleIdioma= i => setIdiomas(p   => p.includes(i) ? p.filter(x=>x!==i) : [...p,i]);
  const toggleCurso = c => setCursos(p    => p.includes(c) ? p.filter(x=>x!==c) : [...p,c]);

  const adicionarCurso = () => {
    const c = novoCurso.trim();
    if (c && !cursos.includes(c)) { setCursos(p => [...p, c]); setNovoCurso(''); }
  };

  const adicionarSkill = () => {
    const s = novaSkill.trim();
    if (s && !hardSkills.includes(s)) { setHardSkills(p => [...p, s]); setNovaSkill(''); }
  };

  const onSubmit = async (data) => {
    if (!areas.length) { toast.error('Selecione pelo menos uma área.'); return; }
    setSaving(true);
    try {
      await api.post('/vagas', {
        ...data,
        areas_contratacao: areas,
        quantidade: Number(data.quantidade) || 1,
        req_escolaridade:  data.req_escolaridade  || null,
        req_experiencia:   Number(data.req_experiencia) || 0,
        req_hard_skills:   hardSkills,
        req_soft_skills:   softSkills,
        req_idiomas:       idiomas,
        req_certificacoes: data.req_certificacoes || null,
        req_cursos:        cursos,
        req_descricao:     data.req_descricao || null,
      });
      toast.success('Vaga publicada com sucesso!');
      navigate('/empresa/vagas');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao publicar vaga.');
    }
    setSaving(false);
  };

  return (
    <Layout title="Nova Vaga" subtitle="Publique uma oportunidade para os talentos do Acre">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 max-w-2xl">

        {/* ── Dados básicos ── */}
        <div className="card p-5 space-y-4">
          <h2 className="section-title">📋 Dados da Vaga</h2>
          <div>
            <label className="label">Título da vaga *</label>
            <input className="input" placeholder="Ex: Analista de TI Pleno"
              {...register('titulo', { required: 'Título obrigatório' })}/>
            {errors.titulo && <p className="text-red-500 text-xs mt-1">{errors.titulo.message}</p>}
          </div>
          <div>
            <label className="label">Descrição da vaga</label>
            <textarea className="input min-h-28 resize-none"
              placeholder="Descreva as responsabilidades e o dia a dia da função..."
              {...register('descricao')}/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Cidade *</label>
              <select className="input" {...register('cidade_vaga',{required:'Cidade obrigatória'})}>
                <option value="">Selecione...</option>
                {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              {errors.cidade_vaga && <p className="text-red-500 text-xs mt-1">{errors.cidade_vaga.message}</p>}
            </div>
            <div>
              <label className="label">Nº de vagas</label>
              <input className="input" type="number" min="1" defaultValue="1" {...register('quantidade')}/>
            </div>
            <div>
              <label className="label">Modalidade</label>
              <select className="input" {...register('modalidade')}>
                <option value="presencial">Presencial</option>
                <option value="remoto">Remoto</option>
                <option value="hibrido">Híbrido</option>
              </select>
            </div>
            <div>
              <label className="label">Regime</label>
              <select className="input" {...register('regime_contratacao')}>
                <option value="clt">CLT</option>
                <option value="pj">PJ</option>
                <option value="estagio">Estágio</option>
                <option value="temporario">Temporário</option>
                <option value="freelance">Freelance</option>
              </select>
            </div>
            <div>
              <label className="label">Salário mínimo (R$)</label>
              <input className="input" type="number" placeholder="2000" {...register('salario_min')}/>
            </div>
            <div>
              <label className="label">Salário máximo (R$)</label>
              <input className="input" type="number" placeholder="4000" {...register('salario_max')}/>
            </div>
            <div className="col-span-2">
              <label className="label">Benefícios</label>
              <input className="input" placeholder="Vale refeição, plano de saúde, VT..."
                {...register('beneficios')}/>
            </div>
          </div>
        </div>

        {/* ── Áreas ── */}
        <div className="card p-5">
          <h2 className="section-title mb-1">🎯 Áreas de Contratação *</h2>
          <p className="text-xs text-gray-400 mb-3">Usadas no algoritmo de match — 30% do score final</p>
          <div className="flex flex-wrap gap-2">
            {AREAS.map(a => (
              <button key={a} type="button" onClick={() => toggleArea(a)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  areas.includes(a)
                    ? 'bg-acre-green text-white border-acre-green'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-acre-green'}`}>
                {a}
              </button>
            ))}
          </div>
          {areas.length > 0 && <p className="text-xs text-acre-green mt-2">{areas.length} área(s) selecionada(s)</p>}
        </div>

        {/* ── Pré-requisitos (usados no algoritmo) ── */}
        <div className="card p-5 space-y-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <Brain size={17} className="text-blue-600"/>
            </div>
            <div>
              <h2 className="section-title mb-0">Pré-Requisitos da Vaga</h2>
              <p className="text-xs text-gray-400 mt-0.5">Esses dados alimentam o algoritmo de match multidimensional — impactam 70% do score</p>
            </div>
          </div>

          {/* Badge explicativo */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-2">
            <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5"/>
            <p className="text-xs text-blue-700 leading-relaxed">
              Quanto mais detalhados os pré-requisitos, mais preciso será o match com os candidatos.
              O algoritmo calcula a <strong>compatibilidade em 5 dimensões</strong>: área, localização, escolaridade, experiência e habilidades técnicas.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Escolaridade mínima */}
            <div>
              <label className="label">Escolaridade mínima <span className="text-blue-500 text-xs">(15% do score)</span></label>
              <select className="input" {...register('req_escolaridade')}>
                <option value="">Sem requisito</option>
                {['Ensino Fundamental','Ensino Médio','Técnico','Graduação','Pós-graduação','Mestrado','Doutorado']
                  .map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>

            {/* Experiência mínima */}
            <div>
              <label className="label">Experiência mínima <span className="text-blue-500 text-xs">(15% do score)</span></label>
              <select className="input" {...register('req_experiencia')}>
                <option value="0">Sem requisito</option>
                <option value="1">6 meses – 1 ano</option>
                <option value="2">1 – 2 anos</option>
                <option value="3">2 – 3 anos</option>
                <option value="5">3 – 5 anos</option>
                <option value="7">5 – 7 anos</option>
                <option value="10">Mais de 10 anos</option>
              </select>
            </div>

            {/* ── CURSOS E CERTIFICAÇÕES OBRIGATÓRIAS (campo principal) ── */}
            <div className="col-span-2">
              <label className="label">
                Cursos e Certificações Obrigatórias
                <span className="ml-1 text-xs text-red-500 font-normal">(candidatos sem esses cursos não aparecem no match)</span>
              </label>
              <p className="text-xs text-gray-400 mb-2">
                Selecione os cursos exigidos para a vaga ou adicione um personalizado.
                Exemplos: "Curso de Atendente de Farmácia", "Técnico em Informática", "CNH categoria B"
              </p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {CURSOS_SUGESTOES.map(c => (
                  <button key={c} type="button" onClick={() => toggleCurso(c)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                      cursos.includes(c)
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'bg-white text-gray-500 border-gray-200 hover:border-orange-400 hover:text-orange-600'}`}>
                    {c}
                  </button>
                ))}
              </div>
              {/* Campo livre */}
              <div className="flex gap-2 mb-2">
                <input className="input flex-1 text-sm"
                  placeholder="Curso específico não listado... Ex: Técnico em Mecânica Industrial"
                  value={novoCurso} onChange={e => setNovoCurso(e.target.value)}
                  onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();adicionarCurso();}}}/>
                <button type="button" onClick={adicionarCurso}
                  className="btn-outline text-sm px-4 flex-shrink-0">+ Adicionar</button>
              </div>
              {cursos.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {cursos.map(c => (
                    <span key={c} className="inline-flex items-center gap-1 text-xs bg-orange-100 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full font-medium">
                      📋 {c}
                      <button type="button" onClick={() => toggleCurso(c)} className="hover:text-red-600">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Descrição dos requisitos */}
            <div className="col-span-2">
              <label className="label">Descrição adicional dos requisitos</label>
              <textarea className="input resize-none text-sm" rows={2}
                placeholder="Ex: Para a vaga de atendente de farmácia é necessário ter o curso de atendente de farmácia reconhecido pelo CRF. Para técnico em informática, aceita-se também cursos similares reconhecidos pelo MEC..."
                {...register('req_descricao')}/>
            </div>
          </div>

          {/* Hard Skills */}
          <div>
            <label className="label">Hard Skills requeridas <span className="text-blue-500 text-xs">(20% do score)</span></label>
            <p className="text-xs text-gray-400 mb-2">Selecione ou adicione habilidades técnicas obrigatórias para a vaga</p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {HARD_SKILLS_SUGESTOES.map(s => (
                <button key={s} type="button" onClick={() => toggleSkill(s)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                    hardSkills.includes(s)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400'}`}>
                  {s}
                </button>
              ))}
            </div>
            {/* Campo para adicionar skill personalizada */}
            <div className="flex gap-2">
              <input className="input flex-1 text-sm" placeholder="Outra habilidade técnica..."
                value={novaSkill} onChange={e => setNovaSkill(e.target.value)}
                onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();adicionarSkill();}}}/>
              <button type="button" onClick={adicionarSkill}
                className="btn-outline text-sm px-4 flex-shrink-0">+ Adicionar</button>
            </div>
            {hardSkills.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {hardSkills.map(s => (
                  <span key={s} className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-medium">
                    {s}
                    <button type="button" onClick={() => toggleSkill(s)} className="hover:text-red-500">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Soft Skills */}
          <div>
            <label className="label">Soft Skills desejadas</label>
            <p className="text-xs text-gray-400 mb-2">Habilidades comportamentais importantes para a função</p>
            <div className="flex flex-wrap gap-1.5">
              {['Liderança','Trabalho em equipe','Comunicação assertiva','Proatividade',
                'Gestão do tempo','Criatividade','Atenção aos detalhes','Resiliência',
                'Adaptabilidade','Inteligência emocional','Pensamento crítico','Empatia'].map(s => (
                <button key={s} type="button" onClick={() => toggleSoft(s)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                    softSkills.includes(s)
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-purple-400'}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Idiomas */}
          <div>
            <label className="label">Idiomas necessários</label>
            <div className="flex flex-wrap gap-1.5">
              {IDIOMAS_LISTA.map(i => (
                <button key={i} type="button" onClick={() => toggleIdioma(i)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    idiomas.includes(i)
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-emerald-400'}`}>
                  {i}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={() => navigate(-1)} className="btn-outline flex-1 py-3">Cancelar</button>
          <button type="submit" disabled={saving}
            className="btn-primary flex-1 py-3 disabled:opacity-60 flex items-center justify-center gap-2">
            {saving ? 'Publicando...' : '🚀 Publicar Vaga'}
          </button>
        </div>
      </form>
    </Layout>
  );
}
